import { isDefined } from "@clipboard-health/util-ts";
import {
  type FcfFeedItem,
  useFcfKnockFeedMessages,
} from "@src/appV2/Knock/hooks/useFcfKnockFeedMessages";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  addAppStateChangeListener,
  type AppListenerHandle,
} from "@src/appV2/NativeAdapters/app.adapter";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useRef, useState } from "react";

import { FcfReplacementShiftsBottomSheet } from "./FcfReplacementShiftsBottomSheet";

/**
 * Watches the Knock in-app feed for unread FCF cancellation messages and
 * auto-triggers the replacement-shifts modal when one is found. Expiry (4 h)
 * and sequential queue logic live inside `useFcfKnockFeedMessages`.
 *
 * Must be rendered inside KnockFeedProvider — callers are responsible for
 * gating on Knock token availability.
 */
export function FcfFeedAutoTrigger() {
  const modalState = useModalState("closed");
  const { modalIsOpen, openModal, closeModal } = modalState;
  const { currentItem, onDismiss, onInteract, onArchive } = useFcfKnockFeedMessages();
  const { userId: workerId } = useDefinedWorker();

  // activeItem tracks what's currently shown in the modal. It's separate from
  // currentItem so the modal stays open even when Knock removes an archived item
  // from the local store (which would clear currentItem).
  const [activeItem, setActiveItem] = useState<FcfFeedItem | undefined>(undefined);

  // Guards against logging duplicate MODAL_DISMISSED events when both a button
  // dismiss and an app_closed event fire in the same modal session.
  const dismissLoggedRef = useRef(false);

  useEffect(() => {
    if (isDefined(currentItem) && !modalIsOpen) {
      setActiveItem(currentItem);
      dismissLoggedRef.current = false;
      openModal();
    }
  }, [currentItem, modalIsOpen, openModal]);

  useEffect(() => {
    if (!modalIsOpen || !isDefined(activeItem)) {
      return;
    }

    let handle: AppListenerHandle | undefined;
    let cancelled = false;

    void addAppStateChangeListener(({ isActive }) => {
      if (!isActive && !cancelled && !dismissLoggedRef.current) {
        dismissLoggedRef.current = true;
        logEvent(APP_V2_USER_EVENTS.FCF_REPLACEMENT_MODAL_DISMISSED, {
          cancellation_id: activeItem.params.cancellationId,
          worker_id: workerId,
          dismiss_method: "app_closed",
          dismissed_at: new Date().toISOString(),
          source: "auto_trigger",
        });
      }
    }).then((h) => {
      if (cancelled) {
        void h.remove();
      } else {
        handle = h;
      }
    });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen, activeItem?.params.cancellationId, workerId]);

  const activeItemId = activeItem?.message.id;

  const handleDismiss = useCallback((): void => {
    setActiveItem(undefined);
    // Guard against accidentally dismissing the next queued item when the
    // Knock store already advanced currentItem (e.g. after onNoEligibleShifts).
    if (currentItem?.message.id === activeItemId) {
      void onDismiss();
    }
    closeModal();
  }, [activeItemId, closeModal, currentItem, onDismiss]);

  const handleNoEligibleShifts = useCallback((): void => {
    // Archive server-side so the message won't re-appear on next app open,
    // but don't clear activeItem — the modal stays open showing the empty state.
    void onArchive();
  }, [onArchive]);

  const handleRequestDismissLog = useCallback((): boolean => {
    if (dismissLoggedRef.current) {
      return false;
    }
    dismissLoggedRef.current = true;
    return true;
  }, []);

  if (!isDefined(activeItem)) {
    return null;
  }

  return (
    <FcfReplacementShiftsBottomSheet
      modalState={modalState}
      params={activeItem.params}
      source="auto_trigger"
      onDismiss={handleDismiss}
      onInteract={onInteract}
      onNoEligibleShifts={handleNoEligibleShifts}
      onRequestDismissLog={handleRequestDismissLog}
    />
  );
}
